import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import Icon from "@mdi/react";
import {
  mdiCar,
  mdiAirplane,
  mdiMapMarkerPath,
  mdiClockOutline,
  mdiCheckCircle,
} from "@mdi/js";
import { slugify } from "../../../utils/slugify";
import { SITE_ORIGIN, cityPath, routePath } from "../../../utils/urls";
import { getCityDestinations, titleCase } from "../../../utils/routeData";

import Hatchback from "../../../assets/cars/hatchback.webp";
import Sedan from "../../../assets/cars/sedan.webp";
import SUV from "../../../assets/cars/suv.webp";
import demo from "../../../assets/cities/temp.webp";

const ICON_MAP = {
  mdiCar,
  mdiAirplane,
  mdiMapMarkerPath,
  mdiClockOutline,
  mdiCheckCircle,
};

const VEHICLE_IMAGE_MAP = { Hatchback, Sedan, SUV };

export default function CityView({ onNotFound }) {
  const { cityName } = useParams();

  const [page, setPage] = useState(null); // ✅ no dummy
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;

    async function load() {
      if (!cityName) {
        if (!ignore) {
          onNotFound?.(true);
          setPage(null);
          setLoading(false);
        }
        return;
      }

      setLoading(true);

      try {
        const backendUrl =
          process.env.REACT_APP_BACKEND_URL || "https://customerapi.pulpitcabs.com";

        const res = await fetch(`${backendUrl}/website/get_city_pages`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            city_slug: cityName.toLowerCase(),
          }),
        });

        // ✅ non-200 means not found/invalid → 404 page
        if (!res.ok) {
          if (!ignore) {
            onNotFound?.(true);
            setPage(null);
          }
          return;
        }

        const json = await res.json();
        const cityData = json?.data?.city_data;

        // ✅ 200 but no data → treat as not found
        if (!cityData || Object.keys(cityData).length === 0) {
          if (!ignore) {
            onNotFound?.(true);
            setPage(null);
          }
          return;
        }

        const mappedPage = {
          citySlug: cityData.citySlug || json?.data?.city_slug,
          cityName: cityData.cityName || json?.data?.city_name,
          hero: cityData.hero || {},
          sections: cityData.sections || {},
        };

        if (!ignore) {
          onNotFound?.(false);
          setPage(mappedPage);
        }
      } catch (e) {
        console.error("Error loading city data:", e);
        if (!ignore) {
          onNotFound?.(true);
          setPage(null);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, [cityName, onNotFound]);

  // Tier-1 destinations this city reaches, minus the handful the CMS table
  // already lists, so the hub does not link the same route twice.
  const extraDestinations = useMemo(() => {
    if (!page) return []
    const listed = new Set(
      (page.sections?.popularRoutes?.routes || [])
        .map((r) => {
          const m = /^(.*?)\s+to\s+(.*)$/i.exec(r.linkText || '')
          return m ? slugify(m[2]) : null
        })
        .filter(Boolean)
    )
    return getCityDestinations(page.citySlug || cityName, 24).filter((d) => !listed.has(d))
  }, [page, cityName])

  // City hubs shipped without any structured data. They are the top of the
  // hub-and-spoke model, so give them the same treatment the route pages get:
  // breadcrumbs to place them in the hierarchy, FAQ markup for rich results,
  // and a Service node carrying the published starting fare.
  const citySchema = useMemo(() => {
    if (!page) return null;
    const { cityName: name, hero, sections } = page;
    const url = `${SITE_ORIGIN}${cityPath(page.citySlug || cityName)}`;
    const faqs = sections?.faq?.items?.filter((f) => f?.q && f?.a) || [];
    const rates = sections?.taxiRates?.rates || [];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: `${name} Cab Service`, item: url },
        ],
      },
      {
        "@type": "Service",
        serviceType: "Taxi and outstation cab service",
        name: hero?.title || `Cab Service in ${name}`,
        description: sections?.intro?.paragraphs?.[0] || hero?.subtitle,
        url,
        areaServed: { "@type": "City", name },
        provider: {
          "@type": "Organization",
          name: "Pulpit Cabs",
          url: `${SITE_ORIGIN}/`,
          telephone: "+91-8052080501",
        },
        ...(rates.length
          ? {
              offers: rates.map((r) => ({
                "@type": "Offer",
                name: `${r.type} cab in ${name}`,
                description: r.desc,
                priceCurrency: "INR",
                priceSpecification: {
                  "@type": "UnitPriceSpecification",
                  price: String(r.rate || "").replace(/[^\d.]/g, ""),
                  priceCurrency: "INR",
                  unitCode: "KMT",
                },
              })),
            }
          : {}),
      },
    ];

    if (faqs.length) {
      graph.push({
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      });
    }

    return { "@context": "https://schema.org", "@graph": graph };
  }, [page, cityName]);

  if (loading) {
    return (
      <div className="city-view">
        <div className="city-view__container">
          <div style={{ padding: "4rem", textAlign: "center" }}>Loading...</div>
        </div>
      </div>
    );
  }

  // ✅ If page is null, Cities component will render NotFound (so render nothing here)
  if (!page) return null;

  const { cityName: displayCityName, hero, sections } = page;

  return (
    <div className="city-view">
      {citySchema && (
        <Helmet defer={false}>
          <script type="application/ld+json">{JSON.stringify(citySchema)}</script>
        </Helmet>
      )}
      {/* Hero Image Section */}
      <div className="city-view__hero">
        <img
          src={hero?.imageUrl || demo}
          alt={hero?.imageAlt || `${displayCityName} city skyline`}
          className="city-view__hero-image"
          fetchpriority="high"
          decoding="async"
        />
        <div className="city-view__hero-overlay">
          <h1 className="city-view__hero-title">{hero?.title}</h1>
          <p className="city-view__hero-subtitle">{hero?.subtitle}</p>
        </div>
      </div>

      <div className="city-view__container">
        {/* Introduction Section */}
        <section className="city-view__section">
          <h2 className="city-view__heading">{sections?.intro?.heading}</h2>
          {(sections?.intro?.paragraphs || []).map((p, idx) => (
            <p key={idx} className="city-view__paragraph">
              {p}
            </p>
          ))}
        </section>

        {/* Taxi Rates Section */}
        <section className="city-view__section city-view__section--highlighted">
          <h2 className="city-view__heading">{sections?.taxiRates?.heading}</h2>
          <p className="city-view__paragraph">{sections?.taxiRates?.subText}</p>

          <div className="city-view__rates-grid">
            {(sections?.taxiRates?.rates || []).map((rate, index) => (
              <div
                key={index}
                className={`city-view__rate-card ${
                  rate.highlight ? "city-view__rate-card--highlight" : ""
                }`}
              >
                {rate.highlight && (
                  <span className="city-view__rate-badge-highlight">
                    {rate.highlightText}
                  </span>
                )}

                <div className="h-44 flex items-center justify-center">
                  <img
                    src={VEHICLE_IMAGE_MAP[rate.type] || Sedan}
                    alt={rate.imageAlt || `${rate.type} cab`}
                    className="h-32 object-contain"
                  />
                </div>

                <h3 className="city-view__rate-type">{rate.type}</h3>
                <p className="city-view__rate-model">{rate.desc}</p>
                <div className="city-view__rate-badge">{rate.seats} seats</div>
                <div className="city-view__rate-price">{rate.rate}</div>
                <p className="city-view__rate-best">{rate.bestFor}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Popular Routes Section */}
        <section className="city-view__section">
          <h2 className="city-view__heading">{sections?.popularRoutes?.heading}</h2>
          <p className="city-view__paragraph">{sections?.popularRoutes?.subText}</p>

          <div className="city-view__routes-table">
            <table className="city-view__table">
              <thead>
                <tr>
                  {(sections?.popularRoutes?.table?.headers || []).map((h, idx) => (
                    <th key={idx}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(sections?.popularRoutes?.routes || []).map((route, index) => {
                  const convertRouteToSlug = (linkText) => {
                    if (!linkText)
                      return sections?.popularRoutes?.table?.linkTo || "/bookCab";
                    const match = linkText.match(/(.+?)\s+to\s+(.+)/i);
                    if (match) {
                      const from = slugify(match[1].trim());
                      const to = slugify(match[2].trim());
                      return `/routes/${from}-to-${to}-cab/`;
                    }
                    return sections?.popularRoutes?.table?.linkTo || "/bookCab";
                  };

                  const routeUrl = convertRouteToSlug(route.linkText);

                  return (
                    <tr key={index}>
                      <td>
                        <Link to={routeUrl} className="city-view__route-link">
                          {route.linkText}
                        </Link>
                      </td>
                      <td>{route.distanceAndTime}</td>
                      <td className="city-view__price">{route.price}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* The CMS table lists only ~4 routes, which is too thin for a hub to
              pass anything to its spokes. These are the rest of the Tier-1
              routes out of this city - every one a page that exists. */}
          {extraDestinations.length > 0 && (
            <div className="city-view__route-links">
              <h3 className="city-view__route-links-title">
                More outstation cab routes from {displayCityName}
              </h3>
              <div className="city-view__route-links-grid">
                {extraDestinations.map((dest) => (
                  <Link
                    key={dest}
                    to={routePath(cityName, dest)}
                    className="city-view__route-links-item"
                  >
                    {displayCityName} to {titleCase(dest)} Cab
                  </Link>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Services Section */}
        <section className="city-view__section city-view__section--services">
          <h2 className="city-view__heading">{sections?.services?.heading}</h2>

          <div className="city-view__services-grid">
            {(sections?.services?.items || []).map((service, index) => (
              <div key={index} className="city-view__service-card">
                <div className="city-view__service-icon">
                  <Icon path={ICON_MAP[service.iconKey] || mdiCar} size={4} color="#21A5A0" />
                </div>
                <h3 className="city-view__service-title text-center">{service.title}</h3>
                <p className="city-view__service-description">{service.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Local Areas Section */}
        <section className="city-view__section">
          <h2 className="city-view__heading">{sections?.localAreas?.heading}</h2>
          <p className="city-view__paragraph">{sections?.localAreas?.subText}</p>

          <div className="city-view__local-areas">
            {(sections?.localAreas?.areas || []).map((area, index) => (
              <span key={index} className="city-view__area-tag">
                {area}
              </span>
            ))}
          </div>

          <div className="city-view__local-info">
            {(sections?.localAreas?.infoCards || []).map((card, idx) => (
              <div key={idx} className="city-view__info-card">
                <h3 className="city-view__info-title">{card.title}</h3>
                <p className="city-view__info-text">{card.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Travel Ideas Section */}
        <section className="city-view__section">
          <h2 className="city-view__heading">{sections?.travelIdeas?.heading}</h2>
          <p className="city-view__paragraph">{sections?.travelIdeas?.subText}</p>

          <div className="city-view__travel-grid">
            {(sections?.travelIdeas?.cards || []).map((card, idx) => (
              <div key={idx} className="city-view__travel-card">
                <h3 className="city-view__travel-title">{card.title}</h3>
                <p className="city-view__travel-text">{card.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Airport Section */}
        <section className="city-view__section city-view__section--highlighted">
          <h2 className="city-view__heading">{sections?.airport?.heading}</h2>
          <p className="city-view__paragraph">{sections?.airport?.subText}</p>

          <div className="city-view__cta-buttons">
            <Link
              to={sections?.airport?.primaryCta?.to || "/bookCab"}
              className="city-view__cta-button city-view__cta-button--primary"
            >
              {sections?.airport?.primaryCta?.label || "Book Airport Cab"}
            </Link>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="city-view__section">
          <h2 className="city-view__heading">{sections?.faq?.heading}</h2>
          <p className="city-view__paragraph">{sections?.faq?.subText}</p>

          <div className="city-view__faq-grid">
            {(sections?.faq?.items || []).map((faq, index) => (
              <div key={index} className="city-view__faq-card">
                <h3 className="city-view__faq-question">{faq.q}</h3>
                <p className="city-view__faq-answer">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="city-view__section city-view__section--features">
          <h2 className="city-view__heading">{sections?.whyChoose?.heading}</h2>

          <div className="city-view__features-list">
            {(sections?.whyChoose?.features || []).map((f, idx) => (
              <div key={idx} className="city-view__feature">
                <span className="city-view__feature-icon">
                  <Icon
                    path={ICON_MAP[f.iconKey] || mdiCheckCircle}
                    size={24}
                    color="#ffffff"
                  />
                </span>
                <div>
                  <h4 className="city-view__feature-title">{f.title}</h4>
                  <p className="city-view__feature-text">{f.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="city-view__section city-view__section--cta">
          <div className="city-view__cta-box">
            <h2 className="city-view__cta-title">{sections?.cta?.heading}</h2>
            <p className="city-view__cta-text">{sections?.cta?.subText}</p>

            <div className="city-view__cta-buttons">
              <Link
                to={sections?.cta?.primaryCta?.to || "/bookCab"}
                className="city-view__cta-button city-view__cta-button--primary"
              >
                {sections?.cta?.primaryCta?.label || "Book Cab"}
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
